import { useEffect, useState } from "react";
import { ArrowUp, ArrowLeftRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Preguntas de sentido en calles de doble sentido: qué sentido prohíbe una señal R-101 y qué carriles ocupa el recorrido.
// Mientras no se contesta, el recuadro sale en ámbar y el plan corta los dos sentidos (lo más prudente).
const RUMBOS = ["norte", "noreste", "este", "sureste", "sur", "suroeste", "oeste", "noroeste"];
export const rumboTxt = (deg) => RUMBOS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
const diff = (a, b) => Math.abs((((a - b) % 360) + 540) % 360 - 180);

const Box = ({ answered, title, children, hint, testid }) => (
  <div className={`rounded-lg border p-2.5 space-y-2 ${answered ? "border-slate-200 bg-slate-50" : "border-amber-400 bg-amber-50"}`} data-testid={testid}>
    <div className="text-xs font-semibold text-slate-800">{title}</div>
    <div className="grid gap-1.5">{children}</div>
    {hint && <div className="text-[0.625rem] text-slate-500 leading-snug">{hint}</div>}
  </div>
);
const Opt = ({ on, onClick, icon, children, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex items-center gap-2 text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${on ? "border-sky-500 bg-sky-600 text-white" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"}`}>
    <span className="w-5 flex justify-center shrink-0">{icon}</span><span>{children}</span>
  </button>
);

// sirve para la señal R-101 y para el punto de corte (mismo dato: sign_dir y rotation = rumbo en el que no se puede circular)
export const SignDirection = ({ el, onChange, readOnly }) => {
  const corte = el.kind === "cut_point";
  const [lat, lon] = el.coords?.[0] || [];
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (lat == null) return undefined;
    let ok = true;
    setSt(null);
    api.get("/calle", { params: { lat, lon } }).then((r) => ok && setSt(r.data)).catch(() => ok && setSt({ error: true }));
    return () => { ok = false; };
  }, [lat, lon]);
  if (!st) return <div className="text-[0.6875rem] text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Consultando la calle…</div>;
  if (st.error) return null;
  if (!st.doble) return <div className="text-[0.6875rem] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="sign-direction-oneway">{st.calle || "La calle"} es de sentido único: {corte ? "el corte" : "la señal"} cierra el paso.</div>;
  const sides = [st.rumbo, (st.rumbo + 180) % 360].map((rot, i) => ({ rot, to: st.hacia?.[i] || "" }));
  const cur = el.sign_dir === "uno" ? sides.reduce((b, o) => (diff(o.rot, el.rotation || 0) < diff(b.rot, el.rotation || 0) ? o : b)) : null;
  const set = (patch) => !readOnly && onChange({ ...el, ...patch });
  return (
    <Box answered={!!el.sign_dir} testid="sign-direction" title={`${st.calle || "Esta calle"} es de doble sentido: ¿qué sentido ${corte ? "se corta" : "prohíbe la señal"}?`}
      hint={corte ? "Solo se desvía el tráfico del sentido cortado; el del contrario sigue circulando sin desvío." : "El tráfico del sentido prohibido se desvía con el mismo cálculo que un corte; el del sentido contrario sigue circulando sin desvío."}>
      <Opt on={el.sign_dir === "ambos"} onClick={() => set({ sign_dir: "ambos" })} icon={<ArrowLeftRight className="w-4 h-4" />} disabled={readOnly} testid="sign-dir-ambos">Los dos sentidos (calle cerrada)</Opt>
      {sides.map((o, i) => (
        <Opt key={i} on={cur === o} onClick={() => set({ sign_dir: "uno", rotation: o.rot })} disabled={readOnly} testid={`sign-dir-${i}`}
          icon={<ArrowUp className="w-4 h-4" style={{ transform: `rotate(${o.rot}deg)` }} />}>
          {corte ? "Solo el sentido hacia" : "Prohibido ir hacia"} {o.to ? <b>{o.to}</b> : "el"} {o.to ? `(${rumboTxt(o.rot)})` : rumboTxt(o.rot)}
        </Opt>
      ))}
    </Box>
  );
};

export const LANES = { ambos: "Los dos sentidos", sentido: "Solo el carril del sentido de la comitiva", contrario: "Solo el carril contrario" };

export const RouteLanes = ({ el, onChange, readOnly }) => {
  const key = JSON.stringify(el.coords || []);
  const [st, setSt] = useState(null);
  useEffect(() => {
    if ((el.coords || []).length < 2) return undefined;
    let ok = true;
    const t = setTimeout(() => api.post("/recorrido/sentidos", { coords: el.coords }).then((r) => ok && setSt(r.data)).catch(() => ok && setSt({ error: true })), 400);
    return () => { ok = false; clearTimeout(t); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st || st.error || !st.doble) return null;
  const set = (v) => !readOnly && onChange({ ...el, carriles: v });
  return (
    <Box answered={!!el.carriles} testid="route-lanes"
      title={`El recorrido pasa por calles de doble sentido (${st.calles.slice(0, 3).join(", ") || "sin nombre"}${st.calles.length > 3 ? "…" : ""}): ¿qué se corta al paso?`}
      hint="Se circula por la derecha: el carril de la comitiva es el de su sentido de marcha (del primer al último punto del dibujo). Las calles del lado libre siguen entrando y saliendo; las del lado ocupado reciben corte previo.">
      {Object.entries(LANES).map(([v, l]) => (
        <Opt key={v} on={(el.carriles || "") === v} onClick={() => set(v)} disabled={readOnly} testid={`route-lanes-${v}`}
          icon={v === "ambos" ? <ArrowLeftRight className="w-4 h-4" /> : <ArrowUp className={`w-4 h-4 ${v === "contrario" ? "rotate-180" : ""}`} />}>{l}</Opt>
      ))}
    </Box>
  );
};
